import { useEffect, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { isLocal, getLocal, putLocal, delLocals } from "@/lib/localPhotos";

const cache = new Map();

async function photoUrl(id) {
  if (isLocal(id)) {
    const blob = await getLocal(id);
    if (!blob) throw new Error("missing");
    return URL.createObjectURL(blob);
  }
  if (!cache.has(id)) {
    cache.set(id, api.get(`/photos/${id}`, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch((e) => { cache.delete(id); throw e; }));
  }
  return cache.get(id);
}

function shrink(file, max = 1600) {
  return new Promise((resolve) => {
    if (!/image\/(jpeg|png|webp)/.test(file.type)) return resolve(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b ? new File([b], "photo.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.82);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

export function Thumb({ id, size = "h-20 w-20" }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    photoUrl(id).then(setSrc).catch(() => setSrc(""));
  }, [id]);
  return (
    <>
      <button type="button" data-testid={`photo-thumb-${id}`} onClick={() => src && setOpen(true)} className={`ka-press relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100 ${size}`}>
        {src ? <img src={src} alt="Evidence" className="h-full w-full object-cover" /> : <Loader2 className="m-auto h-4 w-4 animate-spin text-slate-400" />}
        {isLocal(id) && <span data-testid={`photo-local-${id}`} className="absolute bottom-0 inset-x-0 bg-sky-600/90 text-center text-[9px] font-semibold text-white">On device</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-2"><DialogTitle className="sr-only">Photo evidence</DialogTitle><img src={src} alt="Evidence" className="max-h-[80vh] w-full rounded-lg object-contain" /></DialogContent>
      </Dialog>
    </>
  );
}

export const Photos = ({ ids, size }) => (ids?.length ? <div className="flex flex-wrap gap-2">{ids.map((id) => <Thumb key={id} id={id} size={size} />)}</div> : null);

export function PhotoPicker({ value, onChange, testid = "photo-picker", allowOffline = false }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const ids = [];
    for (const f of files.slice(0, 10 - value.length)) {
      try {
        const fd = new FormData();
        fd.append("file", await shrink(f));
        const { data } = await api.post("/photos", fd);
        ids.push(data.id);
      } catch (err) {
        if (allowOffline && !err?.response) {
          const id = `local:${crypto.randomUUID()}`;
          await putLocal(id, await shrink(f));
          ids.push(id);
          toast.info("No signal — photo saved on this device and will upload with the record", { id: "offline-photo" });
        } else {
          toast.error(errMsg(err));
        }
      }
    }
    onChange([...value, ...ids]);
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {value.map((id) => (
        <div key={id} className="relative">
          <Thumb id={id} size="h-16 w-16" />
          <button type="button" data-testid={`${testid}-remove-${id}`} onClick={() => { delLocals([id]); onChange(value.filter((x) => x !== id)); }} className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900 p-0.5 text-white"><X className="h-3 w-3" /></button>
        </div>
      ))}
      {value.length < 10 && (
        <label data-testid={`${testid}-button`} className="ka-press flex h-16 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-4 text-sm font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {busy ? "Uploading…" : "Add photo"}
          <input data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="hidden" onChange={pick} disabled={busy} />
        </label>
      )}
    </div>
  );
}
